import { parseJsonArray, toStringValue } from '../../(common-lib)/Utils';

export const LIQUIDITY_SECTION_ID = 'upload_proof_of_liquidity';
export const LIQUIDITY_STORAGE_FIELD_ID = 'liquidity_account_items';
export const LIQUIDITY_ACCOUNT_TYPE_FIELD_ID = 'account_type';
export const LIQUIDITY_MOST_RECENT_DOC_ID = 'most_recent_bank_statement_doc';
export const LIQUIDITY_PREVIOUS_DOC_ID = 'previous_bank_statement_doc';
export const MAX_LIQUIDITY_ACCOUNTS = 10;

const LIQUIDITY_DOC_IDS = new Set<string>([LIQUIDITY_MOST_RECENT_DOC_ID, LIQUIDITY_PREVIOUS_DOC_ID]);

export const buildLiquidityDocs = <T extends { documentId?: string }>(
    sectionId: string,
    documents: T[]
): T[] => {
    if (sectionId !== LIQUIDITY_SECTION_ID) return documents;

    const excluded = new Set<string>([LIQUIDITY_MOST_RECENT_DOC_ID, LIQUIDITY_PREVIOUS_DOC_ID]);
    return documents.filter((doc) => !doc.documentId || !excluded.has(doc.documentId));
};

export const deriveLiquidityDocId = (baseDocId: string, accountIndex: number): string => (
    accountIndex === 0 ? baseDocId : `${baseDocId}__${accountIndex + 1}`
);

export const getLiquidityBaseDocId = (docId: string): string => (
    docId.split('__')[0]
);

export const getLiquidityAccountIndex = (docId: string): number => {
    const match = docId.match(/__(\d+)$/);
    if (!match) return 1;
    const suffix = Number(match[1]);
    if (!Number.isFinite(suffix) || suffix <= 0) return 1;
    return suffix;
};

export const isLiquidityDocId = (docId: string): boolean => {
    const baseId = getLiquidityBaseDocId(docId);
    return LIQUIDITY_DOC_IDS.has(baseId);
};

export type BuildLiquidityUploadedDocsOptions = {
    sectionTitle: string;
    documentNameById: Record<string, string | undefined>;
};

export const buildLiquidityUploadedDocs = <TUploaded, TItem>(
    docIdToUploadedDoc: Record<string, TUploaded | undefined>,
    _options: BuildLiquidityUploadedDocsOptions,
    buildItem: (docId: string, uploadedDoc: TUploaded, meta: { baseDocId: string }) => TItem
): TItem[] => {
    const entries = Object.entries(docIdToUploadedDoc)
        .filter(([docId, value]) => Boolean(value) && isLiquidityDocId(docId));

    return entries.map(([docId, value]) => {
        const uploadedDoc = value as TUploaded;
        const baseDocId = getLiquidityBaseDocId(docId);
        return buildItem(docId, uploadedDoc, { baseDocId });
    });
};

type LiquiditySectionDocument = {
    documentId: string;
    name?: string;
};

export type LiquiditySectionMeta = {
    sectionTitle?: string;
    documents?: LiquiditySectionDocument[];
};

export const buildLiquidityDocumentsTabItems = <TUploaded, TItem>(
    docIdToUploadedDoc: Record<string, TUploaded | undefined>,
    sectionMeta: LiquiditySectionMeta | undefined,
    buildItem?: (
        docId: string,
        uploadedDoc: TUploaded,
        meta: { baseDocId: string; templateName: string; sectionTitle: string }
    ) => TItem
): TItem[] => {
    const sectionTitle = sectionMeta?.sectionTitle || 'Upload Proof of Liquidity';
    const documentNameById = (sectionMeta?.documents ?? []).reduce<Record<string, string | undefined>>(
        (acc, doc) => {
            acc[doc.documentId] = doc.name;
            return acc;
        },
        {}
    );

    return Object.entries(docIdToUploadedDoc)
        .filter(([docId, value]) => Boolean(value) && isLiquidityDocId(docId))
        .map(([docId, value]) => {
            const uploadedDoc = value as TUploaded;
            const baseDocId = getLiquidityBaseDocId(docId);
            const templateName = documentNameById[baseDocId] || '';

            if (buildItem) {
                return buildItem(docId, uploadedDoc, { baseDocId, templateName, sectionTitle });
            }

            const blobName = (uploadedDoc as any).uniqueID || (uploadedDoc as any).id || docId;
            return {
                id: docId,
                name: (uploadedDoc as any).fileName || templateName || 'Bank Statement',
                fileName: (uploadedDoc as any).fileName || '',
                uploadDate: (uploadedDoc as any).uploadDate || new Date().toISOString(),
                fileType: (uploadedDoc as any).fileType || 'application/octet-stream',
                documentId: docId,
                section: LIQUIDITY_SECTION_ID,
                sectionTitle,
                blobName,
                templateName,
            } as TItem;
        });
};

export const initializeLiquidityDocs = (
    sectionId: string,
    loanData: { getRawLoanData?: () => unknown },
    documents: { id: string }[],
    docIdToUploadedDoc: Record<string, unknown>,
    copyLoanDataToDocument: (loanData: any, docId: string) => unknown
): boolean => {
    if (sectionId !== LIQUIDITY_SECTION_ID) return false;

    const rawLoanData = loanData?.getRawLoanData?.();
    const liquidityEntriesRaw = (rawLoanData as any)?.borrowerInformation?.[LIQUIDITY_STORAGE_FIELD_ID];
    const liquidityEntries = parseEntries<Record<string, unknown>>(liquidityEntriesRaw)
        .filter((entry) => entry && typeof entry === 'object' && !Array.isArray(entry));

    const accountCount = Math.min(
        MAX_LIQUIDITY_ACCOUNTS,
        Math.max(1, liquidityEntries.length || 1)
    );

    documents.forEach((document) => {
        for (let accountIndex = 0; accountIndex < accountCount; accountIndex += 1) {
            const derivedDocId = deriveLiquidityDocId(document.id, accountIndex);
            docIdToUploadedDoc[derivedDocId] = copyLoanDataToDocument(loanData as any, derivedDocId);
        }
    });
    return true;
};

export const copyLiquidityAccountsToBorrowerInformation = (
    rawLoanData: any,
    value: unknown
): boolean => {
    const entries = parseEntries<Record<string, unknown>>(value)
        .filter((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))
        .map((entry) => {
            const record = entry as Record<string, unknown>;
            return {
                [LIQUIDITY_ACCOUNT_TYPE_FIELD_ID]: toStringValue(record[LIQUIDITY_ACCOUNT_TYPE_FIELD_ID]).trim(),
            };
        })
        .filter((entry) => entry[LIQUIDITY_ACCOUNT_TYPE_FIELD_ID].trim() !== '');

    const borrowerInformation = ((rawLoanData as any).borrowerInformation ??= {});
    if (entries.length === 0) {
        delete borrowerInformation[LIQUIDITY_STORAGE_FIELD_ID];
        delete borrowerInformation[LIQUIDITY_ACCOUNT_TYPE_FIELD_ID];
        if (Object.keys(borrowerInformation).length === 0) {
            delete (rawLoanData as any).borrowerInformation;
        }
        return true;
    }

    borrowerInformation[LIQUIDITY_STORAGE_FIELD_ID] = entries;
    borrowerInformation[LIQUIDITY_ACCOUNT_TYPE_FIELD_ID] = entries[0][LIQUIDITY_ACCOUNT_TYPE_FIELD_ID];
    return true;
};

export const getLiquidityAccountEntries = (rawLoanData: any): Record<string, string>[] => {
    const stored = rawLoanData?.borrowerInformation?.[LIQUIDITY_STORAGE_FIELD_ID];
    if (!stored) return [];

    const parsed = parseEntries<Record<string, unknown>>(stored);
    return parsed
        .filter((entry) => entry && typeof entry === 'object' && !Array.isArray(entry))
        .map((entry) => {
            const record = entry as Record<string, unknown>;
            return {
                [LIQUIDITY_ACCOUNT_TYPE_FIELD_ID]: toStringValue(record[LIQUIDITY_ACCOUNT_TYPE_FIELD_ID]).trim(),
            };
        });
};

const parseEntries = <T,>(raw: unknown): T[] => {
    return parseJsonArray<T>(raw);
};
